// React hooks around the trackers. Each one starts tracking when it has a player and the
// content, and stops - reporting how far playback got - when either changes or the
// component unmounts.
//
// Ported from the backend's activity-tracking-frontend-guide.md (version 2), with one change:
// useVimeoActivity() picks up playback already under way only on a player it has not tracked
// before (see trackVimeoPlayer).

import { useEffect, useRef } from "react";
import { trackMediaElement, trackVimeoPlayer } from "./adapters";
import { activity } from "./instance";

/**
 * Track a Vimeo player. Pass null until the player exists.
 *
 * @param {import("@vimeo/player").default | null | undefined} player
 * @param {import("./playback").PlayableContent | null | undefined} content
 */
export function useVimeoActivity(player, content) {
  const type = content?.type;
  const id = content?.id;
  const startSeconds = content?.startSeconds ?? null;
  const endSeconds = content?.endSeconds ?? null;
  // What was tracked last. The same player given another id is loading that video with
  // loadVideo() and still plays the previous one meanwhile - nothing to pick up there.
  const tracked = useRef({ player: null, id: undefined });

  useEffect(() => {
    if (!player || type === undefined || id === undefined) {
      return undefined;
    }

    const pickUpPlayback = tracked.current.player !== player || tracked.current.id === id;
    tracked.current = { player, id };

    return trackVimeoPlayer(player, { type, id, startSeconds, endSeconds }, activity, { pickUpPlayback });
  }, [player, type, id, startSeconds, endSeconds]);
}

/**
 * Track an <audio> or <video> element. Pass null until the element exists.
 *
 * @param {HTMLMediaElement | null | undefined} media
 * @param {import("./playback").PlayableContent | null | undefined} content
 */
export function useMediaElementActivity(media, content) {
  const type = content?.type;
  const id = content?.id;
  const startSeconds = content?.startSeconds ?? null;
  const endSeconds = content?.endSeconds ?? null;

  useEffect(() => {
    if (!media || type === undefined || id === undefined) {
      return undefined;
    }

    return trackMediaElement(media, { type, id, startSeconds, endSeconds }, activity);
  }, [media, type, id, startSeconds, endSeconds]);
}

/**
 * Track a player that plays a list of tracks through one <audio> element, like the
 * floating audio player. `findTrack` gets the element and returns what it is playing -
 * look it up by the element's src - or null for anything that is not tracked.
 *
 * @param {HTMLMediaElement | null | undefined} media
 * @param {import("./adapters").ContentResolver} findTrack
 */
export function useAudioPlayerActivity(media, findTrack) {
  const findTrackRef = useRef(findTrack);

  useEffect(() => {
    findTrackRef.current = findTrack;
  }, [findTrack]);

  useEffect(() => {
    if (!media) {
      return undefined;
    }

    return trackMediaElement(media, (element) => findTrackRef.current(element), activity);
  }, [media]);
}

/**
 * Report a session when a member signs in. Use once, high in the tree, with the signed-in member's id.
 *
 * @param {number | null | undefined} memberId
 */
export function useActivitySession(memberId) {
  useEffect(() => {
    if (memberId) {
      activity.reportSession();
    }
  }, [memberId]);
}
